import { getLocationLevelLabel } from "api-types/locations/locationDisplay";
import type { Lot, LotSpec } from "api-types/modelTypes";
import { isInvalidDataError } from "common/types/structuredErrorResponse";
import { ensureDefined } from "common/utils/assertOfType";
import { preventNonNumericInputVals } from "common/utils/preventNonNumericInputVals";
import { toTitleCase } from "common/utils/strings";
import { getLotSpecLabel } from "lib/components/builders/communities/utils/lotSpecLabels";
import { EXTERNAL_ID_OVERVIEW_MESSAGE } from "lib/constants/messages";
import { useLotDetailsContext } from "lib/state/LotsContext";
import { useUserContext } from "lib/state/UserContextProvider";
import { updateLot } from "lib/storage/lotStorage";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import InputGroupNumber from "ui/forms/InputGroupNumber";
import InputGroupText from "ui/forms/InputGroupText";
import InputLabel from "ui/forms/InputLabel";
import RadioButton from "ui/forms/RadioButton";
import RadioButtonGroup from "ui/forms/RadioButtonGroup";
import Select from "ui/forms/Select";
import useAppContext from "ui/hooks/useAppContext";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import { getLotAddressPreview } from "./lotAddressPreview";
import { LotStatusOptions } from "./LotConstants";
import styles from "./LotDetailsForm.module.scss";
import LotUpdateConfirmationModal from "./LotUpdateConfirmationModal";
import { GARAGE_HANDING_MULTI_UNIT_TOOLTIP, getLotStatusEditability, isLotInMultiUnitLocation } from "./lotUtils";

interface LotDetailsFormFields
  extends Omit<
    Lot,
    | "id"
    | "builderId"
    | "communityId"
    | "configuredHomePlans"
    | "createdAt"
    | "updatedAt"
    | "address"
    | "price"
    | "phase"
    | "block"
    | "depth"
    | "width"
    | "lotStatus"
    | "releasedAt"
    | "area"
    | "series"
    | "externalUrl"
  > {
  allowedCommunityPlanIds?: string[];
  releasedAt?: Date | null;
  discountAmount?: number;
  discountDescription?: string;
  lotStatus?: string | null;
  phase?: string | null;
  block?: string | null;
  price?: number | null;
  address?: string | null;
  width?: number | null;
  depth?: number | null;
  area?: number | null;
  series?: string | null;
  externalUrl?: string | null;
}

const initialFormFields: LotDetailsFormFields = {
  allowedCommunityPlanIds: [],
  externalId: "",
  spec: "none",
  address: "",
  price: 0,
  lotNumber: "",
  phase: "",
  block: "",
  lotStatus: "available",
  lotTypes: [],
  lotFeatures: [],
  series: "",
  width: 0,
  depth: 0,
  area: 0,
  releasedAt: undefined,
  garageHandingDirection: null,
  discountAmount: 0,
  discountDescription: "",
  externalUrl: "",
};

// See https://github.com/higharc/product/pull/4311 for re-adding plan versions
export default function LotDetailsForm() {
  const { builderId, community, inheritedAddress, lot, displayPrices } = useLotDetailsContext();
  const { setUserNotification } = useAppContext();
  const [fields, setFields] = useState<LotDetailsFormFields>(initialFormFields);
  const [fieldErrors, setFieldErrors] = useState<Array<keyof LotDetailsFormFields>>([]);
  const [mirrorChanged, setMirrorChanged] = useState(false);
  const [pending, setPending] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const { hasPermission, builder } = useUserContext();
  const communityMapConfig = builder?.salesConfig?.communityMapConfig;
  const specLabel = getLotSpecLabel("spec", communityMapConfig);
  const modelLabel = getLotSpecLabel("model", communityMapConfig);
  const designationLabel = getLotSpecLabel(fields.spec ?? "none", communityMapConfig);

  const {
    allHomePlans,
    homePlanPhase,
    isLotFieldsEditable,
    isStatusEditable,
    statusDisabledTooltip: statusSelectDisabledTooltip,
  } = getLotStatusEditability({ configuredHomePlans: lot?.configuredHomePlans });

  const permitted = hasPermission("EDIT_LOTS") && isLotFieldsEditable;
  const garageHandingDisabled = isLotInMultiUnitLocation(lot);
  const addressPreview = getLotAddressPreview(fields.address, inheritedAddress, builder?.locationSettings);

  useEffect(() => {
    if (lot) {
      setFields({
        externalId: lot.externalId ?? null,
        allowedCommunityPlanIds: lot.allowedCommunityPlanIds,
        lotNumber: lot.lotNumber,
        lotStatus: lot.lotStatus,
        phase: lot.phase,
        block: lot.block,
        price: lot.price,
        address: lot.address,
        releasedAt: lot.releasedAt,
        width: lot.width,
        depth: lot.depth,
        area: lot.area,
        series: lot.series,
        spec: lot.spec,
        lotFeatures: lot.lotFeatures,
        lotTypes: lot.lotTypes,
        garageHandingDirection: lot.garageHandingDirection ?? null,
        discountAmount: lot.discounts && lot.discounts.length > 0 ? lot.discounts[0].amount : 0,
        discountDescription: lot.discounts && lot.discounts.length > 0 ? lot.discounts[0].description : "",
        externalUrl: lot.externalUrl,
      });
    }
  }, [lot]);

  const releaseFormattedDate = fields.releasedAt && new Date(fields.releasedAt).toLocaleDateString();

  const handleDesignationChange = (evt: ChangeEvent<HTMLInputElement>): void => {
    if (evt.target.checked) {
      const designation = evt.target.value as LotSpec;
      setFields({ ...fields, spec: designation });
    }
  };

  const mirroringDisplayValue = lot?.garageHandingDirection
    ? toTitleCase(lot.garageHandingDirection)
    : `${getLocationLevelLabel(community, builder?.locationSettings)} Plan default`;

  const handleMirrorChange = (evt: ChangeEvent<HTMLInputElement>): void => {
    if (garageHandingDisabled) {
      return;
    }

    setMirrorChanged(true);

    switch (evt.target.value) {
      case "left":
      case "right":
        setFields({ ...fields, garageHandingDirection: evt.target.value });
        break;
      default:
        setFields({ ...fields, garageHandingDirection: null });
        break;
    }
  };

  const handleFormSubmission = async (evt: any): Promise<void> => {
    evt.preventDefault();

    if (allHomePlans?.length && mirrorChanged) {
      setConfirmationModalOpen(true);
    } else {
      return confirmFormSubmission();
    }
  };

  const confirmFormSubmission = async (): Promise<void> => {
    setPending(true);

    const { discountAmount, discountDescription, ...payloadFields } = fields;
    const payload = Object.assign(lot, payloadFields);
    if ((lot.discounts?.length ?? 0) > 0 || ensureDefined(discountAmount) < 0) {
      (payload as any).discount = { amount: discountAmount, description: discountDescription };
    }

    // NOTE: lot.communityId refers to the location the lot belongs to, which could be of any type
    const [, err] = await updateLot(builderId, lot.communityId, lot.id, { lot: payload });

    setPending(false);
    setConfirmationModalOpen(false);
    setMirrorChanged(false);

    if (err) {
      if (isInvalidDataError(err.data)) {
        setFieldErrors(err.data.fields as Array<keyof LotDetailsFormFields>);
      }
      setUserNotification(err);
      return;
    }

    setFieldErrors([]);
    setUserNotification({ message: "Lot updated!", messageType: "success" });
  };

  return (
    <>
      <form onSubmit={handleFormSubmission}>
        <div className={`mt-6 ${styles.lotOverviewFields}`}>
          <fieldset className={`mt-4 ${styles.row}`}>
            <InputGroupText
              type="text"
              labelText="Lot Number"
              inputValue={String(fields.lotNumber ?? "")}
              onChange={(newValue: string) => setFields({ ...fields, lotNumber: newValue })}
              disabled={!permitted}
            />
            <InputGroupText
              type="text"
              labelText="Phase"
              inputValue={String(fields.phase ?? "")}
              onChange={(newValue: string) => setFields({ ...fields, phase: newValue })}
              disabled={!permitted}
            />
            <InputGroupText
              type="text"
              labelText="Block"
              inputValue={String(fields.block ?? "")}
              onChange={(s: string) => setFields({ ...fields, block: s })}
              disabled={!permitted}
            />
          </fieldset>

          <fieldset className={`mt-4 ${styles.row}`}>
            <div className={styles.wrapper}>
              <div className="label">
                <InputLabel text="Status" />
              </div>
              <Select
                value={fields.lotStatus}
                options={LotStatusOptions}
                selectSize="large"
                onChange={(s) => setFields({ ...fields, lotStatus: s as string | null })}
                disabled={!permitted || !isStatusEditable}
                showTooltip={!!statusSelectDisabledTooltip}
                tooltipText={statusSelectDisabledTooltip}
              />
            </div>
            <div className="inputWithRadioGroupContainer">
              <InputGroupText
                title={`Select 'Standard', '${modelLabel}', or '${specLabel}'`}
                type="text"
                labelText="Designation"
                inputValue={designationLabel}
                onChange={() => undefined}
                placeholder="Select a designation"
                disabled
              />
              <div className="ml-3">
                <RadioButtonGroup orientation="horizontal">
                  <RadioButton
                    label="Standard"
                    id="none"
                    checked={fields.spec === "none"}
                    onChange={handleDesignationChange}
                    value="none"
                    disabled={!hasPermission("EDIT_LOTS")}
                  />
                  <RadioButton
                    label={modelLabel}
                    id="model"
                    checked={fields.spec === "model"}
                    onChange={handleDesignationChange}
                    value="model"
                    disabled={!hasPermission("EDIT_LOTS")}
                  />
                  <RadioButton
                    label={specLabel}
                    id="spec"
                    checked={fields.spec === "spec"}
                    onChange={handleDesignationChange}
                    value="spec"
                    disabled={!hasPermission("EDIT_LOTS")}
                  />
                </RadioButtonGroup>
              </div>
            </div>
          </fieldset>

          {displayPrices && (
            <fieldset className={`mt-4 ${styles.row}`}>
              <InputGroupText
                type="text"
                labelText="Price"
                inputValue={String(fields.price ?? "")}
                onChange={(s: string) => setFields({ ...fields, price: parseInt(s, 10) })}
                disabled={!permitted}
              />
              <InputGroupNumber
                labelText="Discount"
                placeholder="0"
                inputValue={fields.discountAmount ?? 0}
                disabled={!permitted || !hasPermission("EDIT_LOT_DISCOUNT")}
                onChange={(value: number | null) => {
                  setFields({ ...fields, discountAmount: value ?? 0 });
                }}
                validate={() => {
                  if ((fields.discountAmount ?? 0) > 0) {
                    return "Discount cannot be a positive number";
                  }
                  return undefined;
                }}
              />
              <div>
                <InputGroupText
                  type="text"
                  labelText="Discount Description"
                  placeholder="Enter a description of the discount"
                  inputValue={fields.discountDescription}
                  disabled={!permitted || !hasPermission("EDIT_LOT_DISCOUNT")}
                  onChange={(value: string) => setFields({ ...fields, discountDescription: value })}
                />
              </div>
            </fieldset>
          )}

          <fieldset className={`mt-4 ${styles.row}`}>
            <InputGroupText
              tooltip={EXTERNAL_ID_OVERVIEW_MESSAGE}
              type="text"
              labelText="External ID"
              inputValue={String(fields.externalId ?? "")}
              onChange={(s: string) => setFields({ ...fields, externalId: s })}
              disabled={!permitted}
            />
            <div>
              <InputGroupText
                type="url"
                labelText="External URL"
                inputValue={fields.externalUrl}
                onChange={(s: string) => setFields({ ...fields, externalUrl: s?.trim() })}
                disabled={!permitted}
                controlledInputState={fieldErrors.includes("externalUrl") ? "error" : undefined}
                tooltip='When clicking "Open Home Plan", buyers will be redirected to this URL.'
              />
            </div>
          </fieldset>

          <section className={`mt-4 mb-6 ${styles.secondaryDetails}`}>
            <InputGroupText
              type="text"
              labelText="Address"
              inputValue={fields.address}
              onChange={(s: string) => setFields({ ...fields, address: s })}
              disabled={!permitted}
              tooltip="Enter full lot address. When empty, the community location's address will be shown instead."
            />
            <p className={styles.addressPreview}>
              Shown on map:{" "}
              <span className={addressPreview.muted ? styles.addressPreviewMuted : styles.addressPreviewValue}>
                {addressPreview.value}
              </span>
              {addressPreview.source && ` (${addressPreview.source})`}
            </p>
            <fieldset className={`mt-4 ${styles.row}`}>
              <InputGroupText
                type="text"
                labelText="Width"
                inputValue={String(fields.width ?? "")}
                onChange={(s: string) => setFields({ ...fields, width: parseInt(s) })}
                onKeyPress={preventNonNumericInputVals}
                disabled={!permitted}
              />
              <InputGroupText
                type="text"
                labelText="Depth"
                inputValue={String(fields.depth ?? "")}
                onChange={(s: string) => setFields({ ...fields, depth: parseInt(s) })}
                onKeyPress={preventNonNumericInputVals}
                disabled={!permitted}
              />
              <InputGroupText
                type="text"
                labelText="Area"
                inputValue={String(fields.area ?? "")}
                onChange={(s: string) => setFields({ ...fields, area: parseInt(s) })}
                onKeyPress={preventNonNumericInputVals}
                disabled={!permitted}
              />
            </fieldset>
            <fieldset className={`mt-4 ${styles.row}`}>
              <InputGroupText
                type="text"
                labelText="Lot Series"
                inputValue={fields.series}
                onChange={(s: string) => setFields({ ...fields, series: s })}
                disabled={!permitted}
              />
              <InputGroupText
                type="text"
                labelText="Lot Features"
                inputValue={fields.lotFeatures}
                onChange={(s: string) => setFields({ ...fields, lotFeatures: s?.split(",") ?? [] })}
                disabled={!permitted}
              />
              <InputGroupText
                type="text"
                labelText="Lot Types"
                inputValue={fields.lotTypes}
                onChange={(s: string) => setFields({ ...fields, lotTypes: s?.split(",") ?? [] })}
                disabled={!permitted}
              />
            </fieldset>
            <fieldset className={`mt-4 ${styles.row}`}>
              {wrapWithTooltip(
                <div className="inputWithRadioGroupContainer">
                  <InputGroupText
                    title="Select garage handing for homes on this lot"
                    type="text"
                    labelText="Garage Handing"
                    inputValue={mirroringDisplayValue}
                    onChange={() => undefined}
                    disabled
                  />
                  <div className="ml-3">
                    {fields && (
                      <RadioButtonGroup orientation="horizontal">
                        <RadioButton
                          label="Left"
                          id="mirror-false"
                          checked={fields.garageHandingDirection === "left"}
                          onChange={handleMirrorChange}
                          value="left"
                          disabled={!permitted || garageHandingDisabled}
                        />
                        <RadioButton
                          label="Right"
                          id="mirror-true"
                          checked={fields.garageHandingDirection === "right"}
                          onChange={handleMirrorChange}
                          value="right"
                          disabled={!permitted || garageHandingDisabled}
                        />
                        <RadioButton
                          label="Default"
                          id="mirror-default"
                          checked={!fields.garageHandingDirection}
                          onChange={handleMirrorChange}
                          value={undefined}
                          disabled={!permitted || garageHandingDisabled}
                        />
                      </RadioButtonGroup>
                    )}
                  </div>
                </div>,
                garageHandingDisabled ? GARAGE_HANDING_MULTI_UNIT_TOOLTIP : undefined,
              )}
            </fieldset>
            <fieldset className={`mt-4 ${styles.row}`}>
              <div className="inputWithRadioGroupContainer">
                <InputGroupText
                  title="Select 'Release' to mark the lot as released."
                  type="text"
                  labelText="Release Date"
                  inputValue={releaseFormattedDate}
                  onChange={() => undefined}
                  placeholder="Not Released"
                  disabled
                />
                <div className="ml-3">
                  <RadioButtonGroup orientation="horizontal">
                    <RadioButton
                      label="Released"
                      id="releaseLot"
                      checked={!!fields.releasedAt}
                      onChange={(evt) => setFields({ ...fields, releasedAt: !evt.target.checked ? null : new Date() })}
                      disabled={!permitted}
                    />
                    <RadioButton
                      label="Unreleased"
                      id="unReleaseLot"
                      checked={!fields.releasedAt}
                      onChange={(evt) => setFields({ ...fields, releasedAt: evt.target.checked ? null : new Date() })}
                      disabled={!permitted}
                    />
                  </RadioButtonGroup>
                </div>
              </div>
            </fieldset>
          </section>
        </div>
        <Button
          title={
            homePlanPhase !== "PREP"
              ? "Lot cannot be edited when a Home Plan is configured on it."
              : "You do not have permissions to edit this lot."
          }
          btnSize="large"
          disabled={!hasPermission("EDIT_LOTS") || pending || (fields.discountAmount ?? 0) > 0}
        >
          {pending ? "Saving..." : "Save"}
        </Button>
      </form>
      <LotUpdateConfirmationModal
        homePlans={lot?.configuredHomePlans ?? []}
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        onSuccess={confirmFormSubmission}
      />
    </>
  );
}
